// What the analytics API answers: normalized social metrics, the funnel, scores, good hours, the Monday report, short links.
import { z } from "zod";
import { IdPageQuery, pageOf, Uuid } from "./common";

const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, "invalid_day");

/** The money of one currency. Figures in two currencies are never added up. */
export const MoneyDto = z.object({ currency: z.string(), minor: z.number().int() });
export type MoneyDto = z.infer<typeof MoneyDto>;

export const AnalyticsRangeQuery = z.object({ from: Day.optional(), to: Day.optional() });
export type AnalyticsRangeQuery = z.infer<typeof AnalyticsRangeQuery>;

// ---------------------------------------------------------------------------
// Social metrics
// ---------------------------------------------------------------------------

export const SOCIAL_METRIC_NAMES = ["impressions", "reach", "likes", "comments", "shares", "clicks", "views"] as const;
export const SocialMetricName = z.enum(SOCIAL_METRIC_NAMES);
export type SocialMetricName = z.infer<typeof SocialMetricName>;

/** Null means the platform did not say. It never means zero. */
export const NormalizedMetrics = z.object({
  impressions: z.number().nullable(),
  reach: z.number().nullable(),
  likes: z.number().nullable(),
  comments: z.number().nullable(),
  shares: z.number().nullable(),
  clicks: z.number().nullable(),
  views: z.number().nullable(),
});
export type NormalizedMetrics = z.infer<typeof NormalizedMetrics>;

/**
 * ok: every number the platform offers came back. partial: some did not. not_collected: nothing was read yet.
 * unsupported: this platform or channel kind offers no numbers at all.
 */
export const METRIC_STATES = ["ok", "partial", "not_collected", "unsupported"] as const;
export const MetricState = z.enum(METRIC_STATES);
export type MetricState = z.infer<typeof MetricState>;

export const SocialMetricsQuery = IdPageQuery.extend({ channelId: Uuid.optional(), from: Day.optional(), to: Day.optional() });
export type SocialMetricsQuery = z.infer<typeof SocialMetricsQuery>;

export const PostMetricsDto = z.object({
  postId: Uuid,
  channelId: Uuid,
  platform: z.string(),
  channelName: z.string(),
  publishedAt: z.string().nullable(),
  permalink: z.string().nullable(),
  excerpt: z.string(),
  creativeId: Uuid.nullable(),
  topicId: Uuid.nullable(),
  state: MetricState,
  metrics: NormalizedMetrics,
  /** Of the numbers this platform has, the ones it did not give for this post. */
  missing: z.array(SocialMetricName),
  /** The numbers this platform never gives. */
  notOffered: z.array(SocialMetricName),
  collectedOn: Day.nullable(),
});
export type PostMetricsDto = z.infer<typeof PostMetricsDto>;
export const PostMetricsPage = pageOf(PostMetricsDto);
export type PostMetricsPage = z.infer<typeof PostMetricsPage>;

export const PostMetricsHistory = z.object({
  postId: Uuid,
  platform: z.string(),
  days: z.array(z.object({ day: Day, metrics: NormalizedMetrics, missing: z.array(SocialMetricName), collectedAt: z.string() })),
});
export type PostMetricsHistory = z.infer<typeof PostMetricsHistory>;

// ---------------------------------------------------------------------------
// Funnel
// ---------------------------------------------------------------------------

export const FUNNEL_DIMENSIONS = ["channel", "campaign", "creative", "topic", "theme", "hook"] as const;
export const FunnelDimension = z.enum(FUNNEL_DIMENSIONS);
export type FunnelDimension = z.infer<typeof FunnelDimension>;

export const FunnelQuery = AnalyticsRangeQuery.extend({ dimension: FunnelDimension.default("channel"), limit: z.coerce.number().int().min(1).max(200).default(50) });
export type FunnelQuery = z.infer<typeof FunnelQuery>;

export const FunnelNumbers = z.object({
  reach: z.number().int(),
  impressions: z.number().int(),
  engagements: z.number().int(),
  clicks: z.number().int(),
  scans: z.number().int(),
  leads: z.number().int(),
  qualified: z.number().int(),
  demos: z.number().int(),
  clients: z.number().int(),
  orders: z.number().int(),
  ordersValue: z.array(MoneyDto),
  spend: z.array(MoneyDto),
  /** Null with no spend, with nobody to divide by, or with spend in two currencies. */
  costPerLead: MoneyDto.nullable(),
  costPerClient: MoneyDto.nullable(),
});
export type FunnelNumbers = z.infer<typeof FunnelNumbers>;

export const FunnelRow = FunnelNumbers.extend({
  /** Null is what could not be traced back to this dimension. */
  key: z.string().nullable(),
  label: z.string().nullable(),
});
export type FunnelRow = z.infer<typeof FunnelRow>;

/**
 * A cost against its target. `other_currency` is a cost the target can't be compared to, or spend in several currencies:
 * those are never added up or converted to give an answer anyway. `over` reports; nothing stops because of it.
 */
export const TARGET_VERDICTS = ["none", "no_data", "within", "over", "other_currency"] as const;
export const TargetVerdict = z.enum(TARGET_VERDICTS);
export type TargetVerdict = z.infer<typeof TargetVerdict>;

export function targetVerdict(input: { cost: MoneyDto | null; spend: readonly MoneyDto[]; target: MoneyDto | null }): TargetVerdict {
  const { cost, spend, target } = input;
  if (!target) return "none";
  if (spend.length > 1) return "other_currency";
  if (!cost) return spend.some((row) => row.currency !== target.currency) ? "other_currency" : "no_data";
  if (cost.currency !== target.currency) return "other_currency";
  return cost.minor <= target.minor ? "within" : "over";
}

export const FunnelTargetCheck = z.object({ target: MoneyDto.nullable(), verdict: TargetVerdict });
export type FunnelTargetCheck = z.infer<typeof FunnelTargetCheck>;
export const FunnelTargets = z.object({ costPerLead: FunnelTargetCheck, costPerClient: FunnelTargetCheck });
export type FunnelTargets = z.infer<typeof FunnelTargets>;

export const FUNNEL_NOTES = ["paid_reach_summed_over_days", "reach_from_impressions", "spend_in_several_currencies", "rows_cut"] as const;

export const FunnelResponse = z.object({
  from: Day,
  to: Day,
  dimension: FunnelDimension,
  total: FunnelNumbers,
  /** The tenant's targets against the total. */
  targets: FunnelTargets,
  /** On a campaign row its own target wins over the tenant's. */
  rows: z.array(FunnelRow.extend({ targets: FunnelTargets })),
  notes: z.array(z.enum(FUNNEL_NOTES)),
});
export type FunnelResponse = z.infer<typeof FunnelResponse>;

// ---------------------------------------------------------------------------
// Scores
// ---------------------------------------------------------------------------

export const SCORE_KINDS = ["topic", "theme", "hook"] as const;
export const ScoreKind = z.enum(SCORE_KINDS);
export type ScoreKind = z.infer<typeof ScoreKind>;

export const ScoresQuery = z.object({ kind: ScoreKind.default("topic"), limit: z.coerce.number().int().min(1).max(200).default(50) });
export type ScoresQuery = z.infer<typeof ScoresQuery>;

export const ScoreRow = z.object({
  id: Uuid,
  label: z.string(),
  code: z.string().nullable(),
  /** Over the tenant's median: 1 is average. For a first phrase, the chance it truly beats the median. Null: too little data. */
  score: z.number().nullable(),
  priority: z.string().nullable(),
  reach: z.number().int().nullable(),
  impressions: z.number().int().nullable(),
  engagements: z.number().int().nullable(),
  clicks: z.number().int().nullable(),
  leads: z.number().int().nullable(),
  orders: z.number().int().nullable(),
  scoredAt: z.string().nullable(),
});
export type ScoreRow = z.infer<typeof ScoreRow>;
export const ScoresResponse = z.object({ kind: ScoreKind, items: z.array(ScoreRow) });
export type ScoresResponse = z.infer<typeof ScoresResponse>;

// ---------------------------------------------------------------------------
// Good hours
// ---------------------------------------------------------------------------

export const GoodHoursReport = z.object({
  /** results: from this tenant's own posts. sector: too few of them yet, so the defaults of its sector. */
  basis: z.enum(["results", "sector"]),
  measuredPosts: z.number().int(),
  neededPosts: z.number().int(),
  computedAt: z.string().nullable(),
  items: z.array(z.object({ weekday: z.number().int().min(1).max(7).nullable(), hour: z.number().int().min(0).max(23), posts: z.number().int().nullable(), rate: z.number().nullable() })),
});
export type GoodHoursReport = z.infer<typeof GoodHoursReport>;

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

export const ReconcileRowDto = z.object({
  source: z.string(),
  metric: z.string(),
  subject: z.string(),
  ours: z.number(),
  theirs: z.number().nullable(),
  currency: z.string().nullable(),
  status: z.enum(["compared", "unavailable"]),
  flagged: z.boolean(),
  detail: z.string().nullable(),
});
export type ReconcileRowDto = z.infer<typeof ReconcileRowDto>;

export const ReconcileResponse = z.object({
  /** Null until the first comparison ran. */
  weekStart: Day.nullable(),
  /** Some figure of the latest week disagrees with the platform. */
  flagged: z.boolean(),
  rows: z.array(ReconcileRowDto),
});
export type ReconcileResponse = z.infer<typeof ReconcileResponse>;

// ---------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------

export const HomeSummary = z.object({
  needs: z.object({
    approvals: z.number().int(),
    leadsOverSla: z.number().int(),
    guardAlerts: z.number().int(),
    healthIssues: z.number().int(),
    figuresDisagree: z.boolean(),
  }),
  week: z.object({ from: Day, to: Day, current: FunnelNumbers, previous: FunnelNumbers, scheduledPosts: z.number().int() }),
});
export type HomeSummary = z.infer<typeof HomeSummary>;

// ---------------------------------------------------------------------------
// Monday report
// ---------------------------------------------------------------------------

export const ReportCreative = z.object({
  key: z.string(),
  label: z.string(),
  reach: z.number().int(),
  clicks: z.number().int(),
  leads: z.number().int(),
  clients: z.number().int(),
  spend: z.array(MoneyDto),
});
export type ReportCreative = z.infer<typeof ReportCreative>;

export const REPORT_STOPPED_KINDS = ["ad", "post", "channel"] as const;
export const ReportStopped = z.object({
  kind: z.enum(REPORT_STOPPED_KINDS),
  name: z.string(),
  /** A stable code when the system stopped it, or the words the person wrote. */
  reason: z.string(),
  at: z.string(),
});
export type ReportStopped = z.infer<typeof ReportStopped>;

export const ReportTodo = z.object({
  /** A stable code the UI can translate; `text` is the same thing in the report's language. */
  code: z.string(),
  params: z.record(z.string(), z.union([z.string(), z.number()])),
  text: z.string(),
});
export type ReportTodo = z.infer<typeof ReportTodo>;

export const ReportPlan = z.object({
  weekStart: Day,
  /** The plan's status, or none when no plan exists for this week. */
  status: z.string(),
  items: z.array(z.object({ day: Day, hour: z.number().int(), channel: z.string(), kind: z.string(), title: z.string() })),
  itemCount: z.number().int(),
});
export type ReportPlan = z.infer<typeof ReportPlan>;

/** Every figure here comes from code. */
export const MondayReportBody = z.object({
  weekStart: Day,
  weekEnd: Day,
  tenantName: z.string(),
  current: FunnelNumbers,
  previous: FunnelNumbers,
  bestCreatives: z.array(ReportCreative),
  stopped: z.array(ReportStopped),
  plan: ReportPlan,
  todo: z.array(ReportTodo),
  /** The figures of the week disagree with the platform (see the reconciliation). */
  figuresDisagree: z.boolean(),
});
export type MondayReportBody = z.infer<typeof MondayReportBody>;

export const MONDAY_REPORT_STATUSES = ["ready", "sending", "sent", "held", "partial", "empty", "no_recipient", "failed"] as const;

export const MondayReportDto = z.object({
  id: Uuid,
  weekStart: Day,
  language: z.string(),
  status: z.enum(MONDAY_REPORT_STATUSES),
  summary: z.string(),
  summarySource: z.enum(["ai", "code"]),
  summaryReason: z.string().nullable(),
  generatedAt: z.string(),
  sentAt: z.string().nullable(),
  /** How many people the letter reached, how many it waits for behind quiet hours, and how many it did not reach. No addresses. */
  delivered: z.number().int(),
  held: z.number().int(),
  undelivered: z.number().int(),
  /** A send failed and the hourly job will try this week again by itself. */
  retrying: z.boolean(),
  body: MondayReportBody,
});
export type MondayReportDto = z.infer<typeof MondayReportDto>;

export const MondayReportListItem = MondayReportDto.omit({ body: true });
export type MondayReportListItem = z.infer<typeof MondayReportListItem>;
export const MondayReportPage = pageOf(MondayReportListItem);
export type MondayReportPage = z.infer<typeof MondayReportPage>;
export const ReportListQuery = IdPageQuery;
export type ReportListQuery = z.infer<typeof ReportListQuery>;

export const RenderedReport = z.object({ subject: z.string(), html: z.string(), text: z.string() });
export type RenderedReport = z.infer<typeof RenderedReport>;

export const AgencyReportRow = z.object({
  tenantId: Uuid,
  tenantName: z.string(),
  /** False when the tenant has no report for this week yet. */
  hasReport: z.boolean(),
  current: FunnelNumbers.nullable(),
  figuresDisagree: z.boolean(),
  todoCount: z.number().int(),
  bestCreative: ReportCreative.nullable(),
});
export type AgencyReportRow = z.infer<typeof AgencyReportRow>;

export const AgencyReportDto = z.object({
  agencyId: Uuid,
  agencyName: z.string(),
  weekStart: Day,
  status: z.string().nullable(),
  totals: z.object({ spend: z.array(MoneyDto), leads: z.number().int(), qualified: z.number().int(), demos: z.number().int(), clients: z.number().int() }),
  tenants: z.array(AgencyReportRow),
});
export type AgencyReportDto = z.infer<typeof AgencyReportDto>;
export const AgencyReportQuery = z.object({ week: Day.optional() });

// ---------------------------------------------------------------------------
// Short links
// ---------------------------------------------------------------------------

const Slug = z.string().trim().min(1).max(100);

/** `utm_campaign=<yyyymm>_<angle>_<objective>`: either pieces for the convention, or a campaign code already made. */
export const CreateShortLink = z
  .object({
    label: z.string().trim().min(1).max(200),
    url: z.string().trim().url().max(2000),
    /** Where it will be seen: facebook, instagram, flyer, table_tent, email. */
    source: Slug,
    /** social, paid_social, qr, email, print. */
    medium: Slug,
    campaign: Slug.optional(),
    angle: Slug.optional(),
    objective: Slug.optional(),
    term: Slug.optional(),
    content: Slug.optional(),
    creativeId: Uuid.optional(),
    topicId: Uuid.optional(),
    expiresAt: z.iso.datetime({ offset: true }).optional(),
  })
  .strict()
  .refine((value) => Boolean(value.campaign) || Boolean(value.angle && value.objective), { message: "campaign_or_angle_and_objective" });
export type CreateShortLink = z.input<typeof CreateShortLink>;

export const UpdateShortLink = z
  .object({ label: z.string().trim().min(1).max(200).optional(), status: z.enum(["active", "disabled"]).optional(), expiresAt: z.iso.datetime({ offset: true }).nullable().optional() })
  .strict();
export type UpdateShortLink = z.input<typeof UpdateShortLink>;

export const ShortLinkDto = z.object({
  id: Uuid,
  code: z.string(),
  label: z.string(),
  /** What to print or post. */
  shortUrl: z.string(),
  /** The address inside the QR: same link, counted as a scan. */
  qrUrl: z.string(),
  baseUrl: z.string(),
  targetUrl: z.string(),
  utm: z.object({ source: z.string(), medium: z.string(), campaign: z.string(), term: z.string().nullable(), content: z.string().nullable(), topic: z.string().nullable() }),
  creativeId: Uuid.nullable(),
  topicId: Uuid.nullable(),
  status: z.enum(["active", "disabled"]),
  expiresAt: z.string().nullable(),
  createdByName: z.string(),
  createdAt: z.string(),
  opens: z.number().int(),
  scans: z.number().int(),
});
export type ShortLinkDto = z.infer<typeof ShortLinkDto>;
export const ShortLinkPage = pageOf(ShortLinkDto);
export type ShortLinkPage = z.infer<typeof ShortLinkPage>;
export const ShortLinkListQuery = IdPageQuery;
export type ShortLinkListQuery = z.infer<typeof ShortLinkListQuery>;

export const ShortLinkQrQuery = z.object({ size: z.coerce.number().int().min(100).max(2000).default(512) });
export const ShortLinkQrDto = z.object({ url: z.string(), src: z.string(), size: z.number().int() });
export type ShortLinkQrDto = z.infer<typeof ShortLinkQrDto>;

export const ShortLinkStats = z.object({
  id: Uuid,
  from: Day,
  to: Day,
  opens: z.number().int(),
  scans: z.number().int(),
  days: z.array(z.object({ day: Day, opens: z.number().int(), scans: z.number().int() })),
});
export type ShortLinkStats = z.infer<typeof ShortLinkStats>;
